import axios from 'axios';
import API_BASE_URL from '../config';

// Configure axios with base URL and interceptors
const api = axios.create({
  baseURL: API_BASE_URL
});

// Add request interceptor to add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Task API calls
export const taskService = {
  getAllTasks: () => api.get('/tasks'),
  
  getTaskById: (id) => 
    api.get(`/tasks/${id}`)
      .then(response => {
        if (!response.data) {
          throw new Error('No response data received');
        }
        return response;
      })
      .catch(error => {
        if (error.response?.status === 404) {
          throw new Error('Task not found');
        }
        throw error;
      }),

  createTask: (taskData) => api.post('/tasks', taskData),
  updateTask: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  getTaskAssignments: (id) => api.get(`/tasks/${id}/assignments`),
  assignTask: (id, assignmentData) => api.post(`/tasks/${id}/assignments`, { assignees: assignmentData.userId }),
  removeAssignment: (id, userId) => api.delete(`/tasks/${id}/assignments/${userId}`),
  getTaskComments: (id) => api.get(`/tasks/${id}/comments`),
  addTaskComment: (id, comment) => api.post(`/tasks/${id}/comments`, { comment }),
  
  // Trash functionality
  moveToTrash: (id) => api.post(`/tasks/${id}/trash`),
  restoreFromTrash: (id) => api.put(`/tasks/${id}/restore`),
  getTrashedTasks: () => api.get('/tasks/trashed'),
  permanentlyDeleteTask: (id) => api.delete(`/tasks/${id}/permanent`),
  
  // Employee task management
  getAssignedTasks: () => api.get('/tasks/assigned'),
  updateTaskStatus: (id, status) => api.put(`/tasks/${id}/status`, status),
  
  requestTaskCompletion: (id) => 
    api.put(`/tasks/${id}/status`, { status: 'awaiting_approval' })
      .then(response => {
        console.log('Completion request response:', response);
        return response;
      })
      .catch(error => {
        console.error('Error requesting task completion:', error.response || error);
        throw error;
      }),
  
  // Admin task approval
  getPendingApprovals: () => api.get('/tasks/pending-approvals'),
  
  approveTask: (id) => 
    api.post(`/tasks/${id}/approve`)
      .then(response => {
        if (!response || !response.data) {
          throw new Error('No response received from server');
        }
        
        if (!response.data.success) {
          if (response.data.message?.includes('not found') || 
              response.data.message?.includes('not awaiting approval')) {
            throw new Error(response.data.message);
          }
          throw new Error('Failed to approve task: ' + (response.data.message || 'Unknown error'));
        }
        
        return response.data;
      })
      .catch(error => {
        console.error('Error approving task:', error);
        
        // Handle specific error cases
        if (error.response?.status === 404) {
          throw new Error('Task not found or already approved');
        }
        
        if (error.response?.status === 500) {
          throw new Error('Server error while approving task. The task may have been approved - please check the task status.');
        }
        
        // For network errors
        if (error.message === 'Network Error') {
          throw new Error('Network error. Please check your connection and the task status.');
        }
        
        // For any other errors
        throw new Error(error.message || 'Failed to approve task. Please check the task status.');
      }),
  
  rejectTask: (id) => 
    api.post(`/tasks/${id}/reject`)
      .catch(error => {
        console.error('Error rejecting task:', error.response || error);
        throw error;
      }),
  
  // Analytics endpoints
  getEmployeeAnalytics: () => api.get('/analytics/employees'),
  getEmployeeDetails: async (employeeId, timeRange = 'month') => {
    try {
      const response = await axios.get(`${API_BASE_URL}/analytics/employees/${employeeId}?timeRange=${timeRange}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  getDepartmentAnalytics: (departmentId, timeRange) => 
    api.get(`/analytics/departments/${departmentId}?timeRange=${timeRange}`),
  getCompanyAnalytics: (timeRange) => 
    api.get(`/analytics/company?timeRange=${timeRange}`),
  getPerformanceAnalytics: () => api.get('/analytics/performance'),
};

// User API calls
export const userService = {
  getAllUsers: () => api.get('/users'),
  getUserById: (id) => api.get(`/users/${id}`),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  registerUser: (userData) => api.post('/auth/register', userData),
  deleteUser: (id) => api.delete(`/users/${id}`)
};

// Department API calls
export const departmentService = {
  getAllDepartments: () => api.get('/departments'),
  getDepartmentById: (id) => api.get(`/departments/${id}`),
  createDepartment: (departmentData) => api.post('/departments', departmentData),
  updateDepartment: (id, departmentData) => api.put(`/departments/${id}`, departmentData),
  deleteDepartment: (id) => api.delete(`/departments/${id}`),
  getDepartmentTasks: (id) => api.get(`/departments/${id}/tasks`),
  getDepartmentMembers: (id) => api.get(`/departments/${id}/members`)
};

export default api;
